import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FeatureCollection, LineString, Feature } from 'geojson';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class GeojsonService {
  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  fetchGeoJson(south, west, north, east) {
    const bbox = `${south},${west},${north},${east}`;
    console.log('bbox:');
    console.log(bbox);
    return this.http
      .get(
        `https://overpass-api.de/api/interpreter?data=[out:json];way["highway"](${bbox});out geom;
`
      )
      .pipe();
  }

  convertToGeoJson(overpassData: any): FeatureCollection<LineString> {
    const features: Feature<LineString>[] = overpassData.elements
      .filter((element: any) => element.type === 'way' && element.geometry)
      .map((way: any) => {
        const coordinates = way.geometry.map((point: any) => [
          point.lon,
          point.lat,
        ]);
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coordinates,
          },
          properties: {},
        };
      });

    return {
      type: 'FeatureCollection',
      features: features,
    };
  }

  findNearestCoordinates(
    geoJsonData: FeatureCollection<LineString>,
    lat: number,
    lon: number
  ): [number, number] | null {
    let nearestCoord: [number, number] | null = null;
    let minDistance = Infinity;

    geoJsonData.features.forEach((feature: Feature<LineString>) => {
      feature.geometry.coordinates.forEach((coord: [number, number]) => {
        const distance = this.haversineDistance(
          [lat, lon],
          [coord[1], coord[0]]
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearestCoord = coord;
        }
      });
    });

    return nearestCoord;
  }

  private haversineDistance(
    [lat1, lon1]: [number, number],
    [lat2, lon2]: [number, number]
  ): number {
    const toRad = (x: number) => (x * Math.PI) / 180;

    const R = 6371e3;
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δφ = toRad(lat2 - lat1);
    const Δλ = toRad(lon2 - lon1);

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  async saveGeoJson(geoJsonData: any): Promise<void> {
    const geoJsonString = JSON.stringify(geoJsonData);

    try {
      const fileName = 'geojson.json';
      let file = await Filesystem.writeFile({
        path: fileName,
        data: geoJsonString,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
        recursive: true,
      });
      await this.storageService.set('geojson', fileName);
      console.log('GeoJSON saved successfully.');
    } catch (error) {
      console.error('Error saving GeoJSON file:', error);
    }
  }

  async getGeoJson(): Promise<any> {
    try {
      let fileName = await this.storageService.get('geojson');
      const file = await Filesystem.readFile({
        path: fileName,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      let fileData = file.data + '';
      return JSON.parse(fileData);
    } catch (error) {
      console.error('Error reading GeoJSON file:', error);
      return null;
    }
  }
}
